import { useMemo, useRef, useState, useEffect } from 'react'
import * as THREE from 'three/webgpu'
import { useControls } from 'leva'
import { useFrame, useThree } from '@react-three/fiber'
import OceanWaterTSL from './OceanWaterTSL.jsx'

// Water is imported lazily, and only on the WebGL path.
//
// three/examples/jsm/objects/Water.js imports from plain 'three'. A static import here would pull
// the WebGL build into the bundle alongside three/webgpu on BOTH paths — three then warns
// "Multiple instances of Three.js being imported", and objects built from one build can draw
// nothing under a renderer from the other. Loading it on demand keeps the WebGPU path on a
// single three build.
let WaterCtor = null

// Detect mobile at module load time — halve the water reflection RT on mobile (~3 MB saved)
const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth <= 768

// Module-scope constant: a default array literal would be a new reference on every render,
// which invalidates the water's useMemo and rebuilds the ocean continuously.
const DEFAULT_POSITION = [0, 0.5, 0]

export default function OceanGround({ position = DEFAULT_POSITION }) {
  const waterRef = useRef()

  // three's Water example is a GLSL ShaderMaterial whose onBeforeRender reads WebGL-internal
  // renderer properties (`properties.get(...).buffers`), which do not exist on WebGPU — it throws
  // once per frame from inside WebGPURenderer.render(). There is no WebGPU port of it upstream.
  // On WebGPU we substitute a flat sea of the same colour; the WebGL path is unchanged.
  const isWebGPU = useThree(state => !!state.gl?.isWebGPURenderer)

  // The loading screen waits on window.__oceanReady (see SceneReadySignal in app.jsx). Only the
  // WebGPU path defers the ocean behind the shoreline bake, so mark it ready immediately here.
  useEffect(() => {
    if (!isWebGPU) window.__oceanReady = true
  }, [isWebGPU])

  // Pull in the WebGL-only Water class on demand. Never loaded on the WebGPU path, which is the
  // point — see the note on WaterCtor above.
  const [waterLoaded, setWaterLoaded] = useState(!!WaterCtor)
  useEffect(() => {
    if (isWebGPU || WaterCtor) return
    let cancelled = false
    import('three/examples/jsm/objects/Water.js').then((mod) => {
      WaterCtor = mod.Water
      if (!cancelled) setWaterLoaded(true)
    })
    return () => { cancelled = true }
  }, [isWebGPU])

  const {
    animationSpeed,
    waterColor,
    sunColor,
    distortionScale,
    size,
    foamAmount,
    foamDepth,
    foamSharpness,
    foamColor,
  } = useControls('Ocean Ground', {
    animationSpeed: { value: 0.3, min: 0, max: 5, step: 0.1, label: 'Animation Speed' },
    waterColor: { value: '#001d28', label: 'Water Color' },
    sunColor: { value: '#ffffff', label: 'Sun Color' },
    distortionScale: { value: 1.7, min: 0, max: 10, step: 0.1, label: 'Distortion Scale' },
    // Higher value = smaller, denser waves (it multiplies world position before the noise
    // lookup). The old max of 50 sat exactly on the default, so waves could not be made
    // smaller without raising the ceiling.
    size: { value: 50.0, min: 0.1, max: 400, step: 0.1, label: 'Wave Size' },

    // Shoreline foam — WebGPU only (depth-difference technique, see OceanWaterTSL).
    // The WebGL Water shader has no equivalent, so these are inert on that path.
    // Dialled in by Luke against the live WebGPU scene, 2026-07-21 — the approved look.
    foamAmount: { value: 0.11, min: 0, max: 1, step: 0.01, label: 'Foam Amount' },
    foamDepth: { value: 1.30, min: 0.05, max: 10, step: 0.05, label: 'Foam Depth' },
    foamSharpness: { value: 4.5, min: 0.5, max: 8, step: 0.1, label: 'Foam Sharpness' },
    foamColor: { value: '#ffffff', label: 'Foam Color' },

  })

  // Create Water instance
  const water = useMemo(() => {
    if (isWebGPU || !WaterCtor) return null

    // Reduce plane size to improve precision
    const waterGeometry = new THREE.PlaneGeometry(2000, 2000, 128, 128)

    const waterInstance = new WaterCtor(waterGeometry, {
      textureWidth: IS_MOBILE ? 512 : 1024,
      textureHeight: IS_MOBILE ? 512 : 1024,
      waterNormals: new THREE.TextureLoader().load(
        'https://threejs.org/examples/textures/waternormals.jpg',
        (texture) => {
          texture.wrapS = texture.wrapT = THREE.RepeatWrapping
        }
      ),
      sunDirection: new THREE.Vector3(),
      sunColor: sunColor,
      waterColor: waterColor,
      distortionScale: distortionScale,
      fog: false,
    })
    
    waterInstance.rotation.x = -Math.PI / 2
    waterInstance.position.set(position[0], position[1], position[2])
    
    // Add render order to prevent z-fighting
    waterInstance.renderOrder = -1
    
    // Enable polygon offset to push water slightly back in depth buffer
    waterInstance.material.polygonOffset = true
    waterInstance.material.polygonOffsetFactor = 1
    waterInstance.material.polygonOffsetUnits = 1
    
    // Enable transparency for faded edges
    waterInstance.material.transparent = true
    waterInstance.material.depthWrite = false // Prevent z-fighting with transparent water
    
    return waterInstance
  }, [position[0], position[1], position[2], isWebGPU, waterLoaded])
  
  // Update water properties
  useFrame((state) => {
    if (waterRef.current?.material?.uniforms) {
      waterRef.current.material.uniforms['time'].value += animationSpeed * 0.01
      waterRef.current.material.uniforms['sunDirection'].value.set(1, 1, 1).normalize()
      waterRef.current.material.uniforms['sunColor'].value = new THREE.Color(sunColor)
      waterRef.current.material.uniforms['waterColor'].value = new THREE.Color(waterColor)
      waterRef.current.material.uniforms['distortionScale'].value = distortionScale
      waterRef.current.material.uniforms['size'].value = size
    }
  })

  // WebGPU: TSL port of the Water shader — animated waves and sun specular via node materials.
  // See OceanWaterTSL for what does and does not carry over from the GLSL original.
  if (isWebGPU) {
    return (
      <OceanWaterTSL
        position={position}
        animationSpeed={animationSpeed}
        waterColor={waterColor}
        sunColor={sunColor}
        distortionScale={distortionScale}
        size={size}
        foamAmount={foamAmount}
        foamDepth={foamDepth}
        foamSharpness={foamSharpness}
        foamColor={foamColor}
      />
    )
  }

  // null until the lazy Water import resolves (WebGL path only).
  if (!water) return null

  return <primitive ref={waterRef} object={water} />
}
